import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as EditIcon } from 'assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import clsx from 'clsx';
import IconButton from 'components/IconButton/IconButton';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import { IMPACT_STATUS } from 'constants/general';
import getNaceLabel from 'utils/getNaceLabel';

import type ISelectSupplierTable from './interfaces/IItemsTable';

import styles from './scss/ItemsTable.module.scss';

function ItemsTable({
  items,
  headCells,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isInfoTable,
  naceCodeTreeData,
  deleteTemplate,
  handleEdit,
  disableActions,
}: ISelectSupplierTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');

  const scrollRef = useRef<HTMLDivElement>(null);
  const throttleTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    const handleScroll = () => {
      if (throttleTimeout.current) return;
      throttleTimeout.current = setTimeout(() => {
        if (!el || isLoadingMore || !hasMore) {
          throttleTimeout.current = null;
          return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
          onLoadMore();
        }
        throttleTimeout.current = null;
      }, 150);
    };
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
        throttleTimeout.current = null;
      }
    };
  }, [isLoadingMore, hasMore, onLoadMore]);

  return (
    <div className={styles.root}>
      <div
        className={styles.tableBodyScroll}
        ref={scrollRef}
        aria-label="Analysis table body"
        role="region"
      >
        <Table
          className={clsx(styles.table, {
            [styles.tableInfoTable]: isInfoTable,
          })}
        >
          <TableHead className={clsx(styles.header, {
            [styles.headerInfoTable]: isInfoTable,
          })}
          >
            {headCells.map(({
              id, label,
            }) => {
              if (id === 'actions' && disableActions) {
                return null;
              }
              return (
                <TableCell head className={styles.tableCell} key={id}>
                  <div>
                    <span className={styles.tableCellLabel}>
                      {t(label)}
                    </span>
                  </div>
                </TableCell>
              );
            })}
          </TableHead>
          <TableBody className={styles.body}>
            {items ? items?.map((item, index) => (
              <TableRow key={[item.id, index].join('_')}>
                <TableCell>
                  <div className={styles.staticCell}>
                    <span>
                      {getNaceLabel(
                        {
                          naceSection: item.naceSection,
                          naceCodeTreeData,
                        },
                      ).naceSectionLabel || hyphen}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {getNaceLabel({
                    naceCode: item.naceCode,
                    naceSection: item.naceSection,
                    naceCodeTreeData,
                  }).naceCodeLabel || hyphen}
                </TableCell>
                <TableCell>
                  {item?.standard?.name || hyphen}
                </TableCell>
                <TableCell>
                  {item?.topic?.title || hyphen}
                </TableCell>
                <TableCell>
                  {t('materialityAssessment.impact') || hyphen}
                </TableCell>
                <TableCell>
                  {item.description || hyphen}
                </TableCell>
                <TableCell>
                  <span className={clsx(styles.status, {
                    [styles.statusPositive]: item?.impact === IMPACT_STATUS.POSITIVE,
                    [styles.statusNegative]: item?.impact === IMPACT_STATUS.NEGATIVE,
                  })}
                  >
                    {item?.impact || hyphen}
                  </span>
                </TableCell>
                {!disableActions && (
                  <TableCell>
                    <div className={styles.actionCell}>
                      <IconButton
                        icon={<EditIcon />}
                        onClick={() => handleEdit(item.id)}
                        disabled={disableActions}
                      />
                      <IconButton
                        icon={<TrashIcon />}
                        onClick={() => deleteTemplate(item.id)}
                        disabled={disableActions}
                      />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            )) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ItemsTable;
